import React, { ReactElement, memo, useMemo } from "react";
import cx from "classnames";

// dls
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as Svg12 } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronDown/12";

// interfaces
import { ItemAddons, ItemVariants } from "@swiggy-private/cart-types";

// helpers
import { getAddonVariantString } from "@cart-ui/helpers/Item";
import { isNonEmptyString } from "@swiggy-private/checkout-core";

// constants
import { TEXTS } from "@cart-ui/constants";
import { format, AriaHidden, ScreenReaderOnly } from "@swiggy-private/core-ui";
import { CART_ITEMS_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { ACCESSIBLITY_TEXTS, FREEBIE_TEXT } from "./constants";

import styles from "./styles/FoodItemInfo.scss";

export interface FoodItemInfoProps {
    className?: string;
    // name of item
    name: string;
    // optional subtext if any
    subText?: string;
    // is freebie item
    isFreebie: boolean;
    // if item can be cusomtised
    isCustomisable: boolean;
    // addons details
    addons: ItemAddons[];
    // variants details
    variants: ItemVariants[];
    // on click onCustomise Action
    onCustomise: VoidFunction;
    // quantity of item
    quantity: number;
    // item type text used for accessibility
    itemTypeText: string;
    // final price of item
    finalPrice: number;
}

function FoodItemInfoCondensed(props: FoodItemInfoProps): ReactElement {
    const itemCustomiseDetail = props.isCustomisable ? getAddonVariantString(props.addons, props.variants) : "";
    const chevronFillColor = isNonEmptyString(itemCustomiseDetail) ? "text_Low_Emphasis" : "text_Med_Emphasis";

    const accessibilityLabel = useMemo(() => {
        return ` ${props.itemTypeText} ${props.name} ${itemCustomiseDetail}
        ${format(ACCESSIBLITY_TEXTS.FOOD_QUANTITY, String(props.quantity))}
        ${format(ACCESSIBLITY_TEXTS.FINAL_PRICE, String(props.finalPrice))}`;
    }, [props.name, props.itemTypeText, props.finalPrice, itemCustomiseDetail, props.quantity]);

    return (
        <div className={cx(styles.container, props.className)}>
            <ScreenReaderOnly as="div" text={accessibilityLabel} />
            <AriaHidden
                as="div"
                className={cx(styles.containerName, styles.condensed)}
                data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_NAME}
            >
                <Text variant="V2.body2.neutral.regular" color="text_High_Emphasis">
                    {props.name}
                </Text>
            </AriaHidden>
            {props.subText && (
                <Text variant="V2.body3.neutral.regular" color="text_Low_Emphasis">
                    {props.subText}
                </Text>
            )}
            {props.isFreebie && (
                <Text
                    variant="V2.body3.neutral.regular"
                    color="text_Low_Emphasis"
                    className={styles.containerFreebieText}
                >
                    {FREEBIE_TEXT}
                </Text>
            )}
            {props.isCustomisable && (
                <button
                    onClick={props.onCustomise}
                    className={styles.containerCustomiseButton}
                    data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_CUSTOMISE_BUTTON}
                >
                    {isNonEmptyString(itemCustomiseDetail) ? (
                        <Text
                            variant="V2.body3.neutral.regular"
                            color="text_Low_Emphasis"
                            className={styles.containerCustomiseButtonText}
                        >
                            {itemCustomiseDetail}
                        </Text>
                    ) : (
                        <Text
                            variant="V2.caption.neutral.black"
                            color="text_Med_Emphasis"
                            className={styles.containerCustomiseButtonText}
                        >
                            {TEXTS.CUSTOMIZE}
                        </Text>
                    )}
                    <GenericIcon
                        Icon={Svg12}
                        fillColor={chevronFillColor}
                        className={styles.containerCustomiseButtonTextChevronDown}
                    />
                </button>
            )}
        </div>
    );
}

export default memo(FoodItemInfoCondensed);
